'use client';

import { useEffect, useState } from 'react';
import {
  ReadonlyURLSearchParams,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import Script from 'next/script';
import urls from '../urls';
import {
  EventClickArea,
  EventClickAttributes,
  EventData,
  PageViewData,
} from './tealium';
import { trackEvent, trackPageView } from '../helpers/tracking';
import { getUtag, trackTealium } from '../src/tracking';
import { getCookie } from 'cookies-next';
import {
  COOKIE_NAME_CONSENT_PRESET,
  formatTrackingString,
} from '@tuic/tracking/src/utils';
import { logger } from '@tuic/logger';

declare global {
  interface Window {
    __tuic_tracker_ready?: boolean;
    __tuic_tracker_page_view_sent?: boolean;
    __tuic_tracker_event_queue?: EventData[];
  }
}

/**
 * Page view parameters. There are some parameters that are required.
 */
type PageViewDataProps = {
  page_platform: PageViewData['page_platform'];
  page_category: PageViewData['page_category'];
  page_login_status?: PageViewData['page_login_status'];
  page_region_code?: PageViewData['page_region_code'];
  page_route_code?: PageViewData['page_route_code'];
  page_route_recommendations?: PageViewData['page_route_recommendations'];
  // page_type?: PageViewData['page_type'];
  page_travel_status?: PageViewData['page_travel_status'];
  page_ship?: PageViewData['page_ship'];
  // trip_code?: PageViewData['trip_code'];
  // trip_date_end?: PageViewData['trip_date_end'];
  // trip_date_start?: PageViewData['trip_date_start'];
  // trip_name?: PageViewData['trip_name'];
  // trip_ship_name?: PageViewData['trip_ship_name'];
  // user_email_hashed?: PageViewData['user_email_hashed'];
} & Partial<PageViewData> & { [k: string]: any };

type TrackingProviderProps = {
  /**
   * Set to true to disable page view tracking.
   */
  disablePageViewTracking?: boolean;
  /**
   * Set to true to disable event tracking.
   */
  disableEventViewTracking?: boolean;
  /**
   * Page view parameters. There are some parameters that are set in the TrackingProvider and some that are required by page/layout consuming the TrackingProvider.
   */
  pageViewParams: PageViewDataProps;
};

export const EVENT_TRACKER_READY = 'tuic:trackerReady';

const trackEventProxy = (
  event: MouseEvent | TouchEvent,
  delay: boolean,
  ...args: Parameters<typeof trackEvent>
) => {
  const href: string | undefined =
    (event.target && 'href' in event.target && (event.target.href as string)) ||
    undefined;

  if (delay && href) {
    event.preventDefault();
    setTimeout(() => {
      window.location.href = href;
    }, 750);
  }
  trackEvent(...args);
};

/**
 * Handler function for click events (click, layer-click and module-click)
 */
const clickTrackingHandler = (event: MouseEvent | TouchEvent) => {
  if (!(event.target instanceof HTMLElement)) return;

  const clickLabel = event.target
    .closest(`[${EventClickAttributes.Label}]`)
    ?.getAttribute(EventClickAttributes.Label);

  const clickElement = event.target
    .closest(`[${EventClickAttributes.Element}]`)
    ?.getAttribute(EventClickAttributes.Element);

  if (!clickLabel && !clickElement) return;

  // graceful error handling for general click tracking
  if (!clickLabel || !clickElement) {
    logger.warn(
      `Tracking: click event failed. data-track-click-label is ${clickLabel} and data-track-click-element is ${clickElement}`,
    );
    return;
  }

  const clickCategory =
    event.target
      .closest(`[${EventClickAttributes.Category}]`)
      ?.getAttribute(EventClickAttributes.Category) || '';
  const clickArea =
    event.target
      .closest(`[${EventClickAttributes.Area}]`)
      ?.getAttribute(EventClickAttributes.Area) || '';
  const clickModule =
    event.target
      .closest(`[${EventClickAttributes.Module}]`)
      ?.getAttribute(EventClickAttributes.Module) || '';
  const clickDialog =
    event.target
      .closest(`[${EventClickAttributes.Dialog}]`)
      ?.getAttribute(EventClickAttributes.Dialog) || '';

  let delay: boolean = false;
  const delayElement = event.target.closest(`[${EventClickAttributes.Delay}]`);
  if (
    delayElement &&
    delayElement.getAttribute(EventClickAttributes.Delay) === 'true' &&
    delayElement.getAttribute('target') !== '_blank'
  ) {
    delay = true;
  }

  // graceful error handling for dialog click tracking
  if (clickDialog && clickArea !== EventClickArea.Dialog) {
    logger.warn(
      `Tracking: layer-click event failed. data-track-dialog is ${clickDialog}, but data-track-click-area found in parents is ${clickArea} (should be "layer")`,
    );
    return;
  } else if (!clickDialog && clickArea === EventClickArea.Dialog) {
    logger.warn(
      `Tracking: layer-click event failed. data-track-click-area="${clickArea}" found in click tracking, but data-track-dialog is ${clickDialog} (not found in parent nodes)`,
    );
    return;
  }

  // graceful error handling for module click tracking
  if (clickModule && clickArea !== EventClickArea.Content) {
    logger.warn(
      `Tracking: module-click event failed. data-track-module is ${clickModule}, but data-track-click-area found in parents is ${clickArea} (should be "module")`,
    );
    return;
  } else if (!clickModule && clickArea === EventClickArea.Content) {
    logger.warn(
      `Tracking: module-click event failed. data-track-click-area="${clickArea}" found in click tracking, but data-track-module is ${clickModule} (not found in parent nodes)`,
    );
    return;
  }

  const click_label = [
    clickArea,
    clickElement,
    formatTrackingString(clickCategory),
    formatTrackingString(clickLabel),
  ]
    .filter(elem => !!elem)
    .join('.');

  if (clickDialog) {
    trackEventProxy(event, delay, {
      event_name: 'layer-click',
      click_label,
      layer_name: formatTrackingString(clickDialog) ?? '',
    });
  } else if (clickModule) {
    trackEventProxy(event, delay, {
      event_name: 'module-click',
      module_item: click_label,
      module_name: formatTrackingString(clickModule) ?? '',
    });
  } else {
    trackEventProxy(event, delay, { event_name: 'click', click_label });
  }
};

type PathBasedHandler<T = unknown> = (
  pathname: string | null,
  searchParams?: ReadonlyURLSearchParams | null,
) => T;

const getPreparedPageViewData: PathBasedHandler<Partial<PageViewData>> = (
  pathname,
  searchParams,
) => {
  return {
    page_name: getUrlPageName(),
    page_country: pathname?.includes('/en/') ? 'en' : 'de',
    page_language: pathname?.includes('/en/') ? 'en' : 'de',
    page_url_parameter: searchParams?.toString(),
    page_test_variant: 'original',
    page_b2b: false,
  };
};

const createClickTrackingHandler: PathBasedHandler<
  (ev: MouseEvent | TouchEvent) => void
> = (pathname, searchParams) => {
  return (event: MouseEvent | TouchEvent) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Click itself isn't possible if located in an area
    const manualTrackData = event.target.getAttribute('data-track-manual');
    if (manualTrackData) {
      let trackData;
      try {
        trackData = JSON.parse(manualTrackData);
      } catch (e) {
        logger.warn(
          `Tracking: manual tracking data is not valid JSON: ${manualTrackData}`,
        );
      }

      if (trackData) {
        return trackEventProxy(event, false, {
          event_name: 'click',
          ...getPreparedPageViewData(pathname, searchParams),
          ...trackData,
        });
      }

      return;
    }

    return clickTrackingHandler(event);
  };
};

export const trackerReadyInWindow = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.__tuic_tracker_ready === true;
};

export const trackingLibTrackerReadyInWindow = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (
    !('__tuic_tracker' in window) ||
    typeof window.__tuic_tracker === 'undefined' ||
    window.__tuic_tracker === null
  ) {
    return false;
  }

  if (
    typeof window.__tuic_tracker !== 'object' ||
    !('ready' in window.__tuic_tracker)
  ) {
    return false;
  }

  if (typeof window.__tuic_tracker.ready !== 'boolean') {
    return false;
  }

  return window.__tuic_tracker.ready;
};

/**
 * The TrackingProvider adds the Tealium scripts and manages the page view and click event tracking.
 * This TrackingProvider works the App Router in Next.js 13+.
 * To support RSCs it doesn't wrap around children but rather should be placed at the bottom of a layout.
 * The TrackingProvider needs to be provided somewhere in the layout tree also for click event tracking.
 * Tracking consists of two parts: page view tracking and event tracking.
 *
 * * Page View Tracking
 * Page view tracking is achieved by adding the TrackingProvider to a layout on a root level.
 * The TrackingProvider is persisted and tracks pathname changes to trigger a new page view.
 * Most page view parameters are defined/reevaluated in the TrackingProvider, but some need to be
 * defined on an app/layout level and passed in as props (page_platform, page_category, page_login_status).
 *
 * * Event Tracking
 * General event tracking is achieved by calling the trackEvent helper function of @tuic/lib/helpers/tracking.
 * A tracking event consists of a required event_name parameter and optional event data parameters (depending on the specified event).
 * There are two types of events: general click tracking events and specific custom events (such as layer-open, video-play or download).
 * General click tracking events are handeled in a generic way in the TrackingProvider.
 * Specific custom events need to be implemented in the component where they should be triggered by calling the trackEvent helper function.
 *
 * * Click Event Tracking
 * Click event tracking should be implemented on all interactive elements of a page.
 * A click tracking event consist of the event_name parameter ('click') and a click_label parameter describing what was clicked.
 * This click_label parameter is constructed with a hiearchy of tracking "levels" - [AREA].[ELEMENT].[CATEGORY].[LABEL]
 * AREA - navi (main navigation) / navi-main (top navigation) / breadcrumb / content / sidebar / footer / layer
 * ELEMENT - button / link / icon / logo / image / video / custom
 * CATEGORY - Through this element the menu headline is given - only applies to main navigation.
 * LABEL - the button, link text, icon name, or description of the picture or video is given.
 * e.g. "navi.button.mein-schiff-entdecken.vielfalt-an-bord"
 * there
 */
const TrackingProvider = ({
  disablePageViewTracking = false,
  disableEventViewTracking = false,
  pageViewParams,
}: TrackingProviderProps) => {
  const tealiumEnv = process.env.NEXT_PUBLIC_TEALIUM_ENV_FLAG ?? 'prod';
  const [trackerIsReady, setTrackerIsReady] = useState(false);
  const [pageViewSent, setPageViewSent] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  let [scriptLoadedByTrackingLibrary, setScriptLoadedByTrackingLibrary] =
    useState<boolean>();

  let handleTrackerScriptLoad = () => {
    window.dispatchEvent(new Event(EVENT_TRACKER_READY));
  };

  useEffect(() => {
    let handleTrackerLoad = () => {
      setTrackerIsReady(true);
      window.__tuic_tracker_ready = true;
    };

    if (window.__tuic_tracker_ready) {
      handleTrackerLoad();
      return;
    }

    window.addEventListener(EVENT_TRACKER_READY, handleTrackerLoad);

    return () => {
      window.removeEventListener(EVENT_TRACKER_READY, handleTrackerLoad);
    };
  }, []);

  // Page View Tracking
  useEffect(() => {
    if (tealiumEnv !== 'dev' && (!trackerIsReady || disablePageViewTracking)) {
      return;
    }

    trackPageView({
      ...getPreparedPageViewData(pathname, searchParams),
      ...pageViewParams,
    });

    setPageViewSent(true);
  }, [pathname, trackerIsReady]);

  let utag = getUtag(typeof window === 'undefined' ? undefined : window);

  // Set consent when consent preset cookie has a value of "1".
  useEffect(() => {
    if (typeof utag === 'undefined') {
      return;
    }

    let cookieValue = getCookie(COOKIE_NAME_CONSENT_PRESET);

    if (cookieValue !== '1') {
      return;
    }

    utag.gdpr.setAllCategories(true);
  }, [utag, trackerIsReady]);

  // Click Event Tracking
  useEffect(() => {
    if (disableEventViewTracking) {
      return;
    }

    if (scriptLoadedByTrackingLibrary !== false) {
      return;
    }

    document.addEventListener(
      'click',
      createClickTrackingHandler(pathname, searchParams),
    );

    return () => {
      document.removeEventListener(
        'click',
        createClickTrackingHandler(pathname, searchParams),
      );
    };
  }, [scriptLoadedByTrackingLibrary, disableEventViewTracking]);

  useEffect(() => {
    if (!pageViewSent) {
      return;
    }

    window.__tuic_tracker_page_view_sent = true;

    let queue = window.__tuic_tracker_event_queue ?? [];
    window.__tuic_tracker_event_queue = [];

    queue.forEach(data => trackTealium(data, 'Event'));
  }, [pageViewSent]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setScriptLoadedByTrackingLibrary(
      !!window.document.querySelector('#tealium-spa-setup'),
    );
  }, []);

  // Prevent loading of the tracking script in development environments.
  if (tealiumEnv === 'dev' || tealiumEnv === 'storybook') return null;

  if (scriptLoadedByTrackingLibrary !== false) {
    return null;
  }

  return (
    <>
      <Script id="tealium-spa-setup">{`
        window.utag_cfg_ovrd = window.utag_cfg_ovrd || {};
        window.utag_cfg_ovrd.noview = true;
      `}</Script>
      <Script
        src={urls.tealiumScriptUrl}
        strategy="afterInteractive"
        async
        onLoad={handleTrackerScriptLoad}
      />
    </>
  );
};

export const getUrlPageName = () => {
  const location = window.location;
  const host = location.host.replaceAll('.', '_'); // 'www_meinschiff_com'
  const slug = location.pathname
    .replace('/de/', '/')
    .replace('/en/', '/')
    .split('/')
    .filter(v => v); // Remove empty values

  return [...[host], ...slug].join('|');
};

/**
 * Extracts the locale from the current URL pathname.
 *
 * Checks if the pathname starts with a two-letter language code (e.g., "/en/", "/de/").
 * If a valid locale is found, it is returned. Otherwise, "de" (German) is used as the default.
 *
 * @returns {string} The detected locale (e.g., "en", "de"). Defaults to "de" if no match is found.
 */
export const getUrlPageLocale = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/([a-zA-Z]{2})(\/|$)/);
  return match ? match[1] : 'de';
};

export default TrackingProvider;
